<template>
	<div class="pronto-profile-picker">
		<ProfilePic
			:key="userRecord.profilepic"
			:user="userRecord"
			:size="size"
			@click="onClickPic"
		/>
		<Menu
			v-if="hasPic"
			class="edit-icon"
			:items="menuItems"
			:boundary="16"
			:offsetY="8"
			left
			@select="onSelectMenuItem"
			ref="menu"
		>
			<img class="edit-icon" src="/images/pronto/edit.svg" alt="Edit" />
		</Menu>
		<img
			class="edit-icon"
			src="/images/pronto/edit.svg"
			alt="Edit"
			@click="onClickPic"
		/>
		<input type="file" accept="image/*" @change="profileChange" ref="input" />
		<div class="loading-spinner" v-if="isLoading">
			<Spinner />
		</div>
	</div>
</template>

<script>
import { normalizeImageForFileKey, uploadDirect } from '@/api/upload'
import Spinner from './Spinner'
import Menu from './Menu'
import ProfilePic from './ProfilePic'
import { imageResizePresets } from '@/helpers/constants'

export default {
	name: 'ProfilePicker',
	components: {
		ProfilePic,
		Spinner,
		Menu,
	},
	props: {
		user: {
			type: Object,
			default: null,
		},
		file: Object,
		size: {
			type: String,
			default: '32px',
		},
	},
	data() {
		return {
			isLoading: false,
			shouldRemove: false,
			tempUrl: this.file ? URL.createObjectURL(this.file) : null,
		}
	},
	emits: ['success', 'remove'],
	computed: {
		cssVars() {
			return { '--size': this.size }
		},
		userRecord() {
			if (this.shouldRemove) {
				return {
					...this.user,
					profilepic: 0,
					profilepicpath: null,
					profilepicurl: null,
				}
			}
			if (this.tempUrl) {
				return {
					...this.user,
					profilepic: 1,
					profilepicpath: null,
					profilepicurl: this.tempUrl,
				}
			}
			return this.user
		},
		hasPic() {
			return !!this.userRecord.profilepic
		},
		menuItems() {
			return [
				{ id: 'change', label: this.$t('common_avatar_change') },
				{ id: 'remove', label: this.$t('common_avatar_remove'), danger: true },
			]
		},
	},

	beforeUnmount() {
		if (this.tempUrl) {
			URL.revokeObjectURL(this.tempUrl)
		}
	},

	methods: {
		async profileChange(e) {
			if (!e.target.files.length) return
			this.$refs.menu?.closeMenu()
			this.isLoading = true
			const file = e.target.files[0]

			if (!file.type?.startsWith('image/')) {
				this.$alert.error('Please select a supported image format')
				this.isLoading = false
				return
			}

			try {
				this.tempUrl = URL.createObjectURL(file)
				const upload = await uploadDirect(file)
				const result = await normalizeImageForFileKey(
					upload.data.key,
					imageResizePresets.PROFILE_PIC
				)
				this.shouldRemove = false
				this.$emit('success', { ...result.normalized, file })
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
				this.$emit('error', e)
				this.tempUrl = null
			}
			this.isLoading = false
		},

		onClickPic() {
			if (this.hasPic) {
				return setTimeout(() => {
					this.$refs.menu?.toggleMenu()
				}, 25)
			}
			this.$refs.input.click()
		},

		onSelectMenuItem({ id }, e) {
			e.stopPropagation()
			if (id === 'change') {
				this.$refs.input.click()
			} else if (id === 'remove') {
				this.tempUrl = null
				this.shouldRemove = true
				this.$emit('remove')
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-profile-picker {
	cursor: pointer;
	position: relative;
	--pic-size: v-bind('size');
	width: var(--pic-size);
	height: var(--pic-size);

	input {
		opacity: 0;
		width: 1px;
		height: 1px;
	}
	.edit-icon {
		font-size: 30px;
		width: 30px;
		height: 30px;
		position: absolute;
		bottom: 0px;
		cursor: pointer;
		right: -8px;
		color: var(--teal-300);
		background-color: white;
		overflow: hidden;
		border-radius: 50%;
		z-index: 2;
		&:hover {
			color: var(--teal-400);
		}
	}
	.icon-placeholder {
		font-size: var(--pic-size);
		color: var(--teal-300);
	}
	.loading-spinner {
		@include absolute-cover;
		@include flex-center;
		width: 100%;
		overflow: hidden;
		border-radius: calc(var(--pic-size) / 2);
		background: rgba(0, 0, 0, 0.4);
		z-index: 1;
		.pronto-spinner {
			font-size: 45px;
		}
	}
}
</style>
